/**
 * <s-ingredient-reveal> — shows key-ingredient callouts over the product gallery.
 * Mouse: opens while the pointer hovers the gallery. Touch: opens on press-and-hold and closes on release
 * (the tap that ends a hold never opens the zoom dialog). The toggle button (command="--toggle") keeps it
 * open for keyboard and screen-reader users. Tags are laid out down both sides and SVG arrows are drawn
 * from each tag toward the product at the centre, recomputed on resize.
 */
const HOLD_MS = 380;
const MOVE_TOLERANCE = 10;

class SIngredientReveal extends HTMLElement {
  #ac;
  #holdTimer = 0;
  #holdStart = null;
  #heldOpen = false;
  #suppressClick = false;
  #pinned = false;
  #resizeObserver;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const surface = this.parentElement;
    if (!surface) return;

    this.addEventListener('command', (event) => {
      if (event.command !== '--toggle') return;
      this.#pinned = !this.#pinned;
      this.#setOpen(this.#pinned);
    }, opts);

    surface.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') this.#setOpen(true);
    }, opts);
    surface.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse' && !this.#pinned) this.#setOpen(false);
    }, opts);

    surface.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      if (event.target instanceof Element && event.target.closest('.dert-reveal__toggle, [command="--prev"], [command="--next"]')) return;
      this.#holdStart = { x: event.clientX, y: event.clientY };
      clearTimeout(this.#holdTimer);
      this.#holdTimer = window.setTimeout(() => {
        this.#heldOpen = true;
        this.#suppressClick = true;
        this.#setOpen(true);
        navigator.vibrate?.(8);
      }, HOLD_MS);
    }, opts);

    surface.addEventListener('pointermove', (event) => {
      if (!this.#holdStart || this.#heldOpen) return;
      const dx = Math.abs(event.clientX - this.#holdStart.x);
      const dy = Math.abs(event.clientY - this.#holdStart.y);
      if (dx > MOVE_TOLERANCE || dy > MOVE_TOLERANCE) this.#cancelHold();
    }, opts);

    const release = () => {
      this.#cancelHold();
      if (this.#heldOpen) {
        this.#heldOpen = false;
        if (!this.#pinned) this.#setOpen(false);
      }
    };
    surface.addEventListener('pointerup', release, opts);
    surface.addEventListener('pointercancel', release, opts);

    // A long-press must not open the image zoom or the system image menu.
    surface.addEventListener('click', (event) => {
      if (!this.#suppressClick) return;
      this.#suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, { capture: true, signal: this.#ac.signal });
    surface.addEventListener('contextmenu', (event) => {
      if (this.#heldOpen || this.#holdStart) event.preventDefault();
    }, opts);

    this.#resizeObserver = new ResizeObserver(() => this.#layout());
    this.#resizeObserver.observe(this);
    this.#layout();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#resizeObserver?.disconnect();
    clearTimeout(this.#holdTimer);
  }

  #cancelHold() {
    clearTimeout(this.#holdTimer);
    this.#holdStart = null;
  }

  #setOpen(open) {
    if (open) this.#layout();
    this.toggleAttribute('data-open', open);
    const toggle = this.querySelector('.dert-reveal__toggle');
    toggle?.setAttribute('aria-pressed', String(this.#pinned && open));
  }

  #layout() {
    const box = this.getBoundingClientRect();
    if (!box.width || !box.height) return;
    const svg = this.querySelector('.dert-reveal__lines');
    svg?.setAttribute('viewBox', `0 0 ${box.width} ${box.height}`);

    const tags = [...this.querySelectorAll('[data-reveal-tag]')];
    const perSide = { start: [], end: [] };
    for (const tag of tags) perSide[tag.dataset.side === 'end' ? 'end' : 'start'].push(tag);

    const rtl = getComputedStyle(this).direction === 'rtl';
    const cx = box.width / 2;
    const cy = box.height / 2;

    for (const side of ['start', 'end']) {
      const group = perSide[side];
      group.forEach((tag, index) => {
        // Spread tags between 12% and 72% of the height, staggered per side, clear of the toggle.
        const slots = group.length;
        const offset = side === 'end' ? 0.09 : 0;
        const t = slots === 1 ? 0.36 + offset : 0.12 + offset + (index * (0.52 / Math.max(1, slots - 1)));
        tag.style.top = `${Math.min(0.72, t) * 100}%`;
        tag.style.transform = '';
      });
    }

    // Measure after positioning, then draw each arrow from the tag's inner edge toward the product.
    tags.forEach((tag) => {
      const i = tag.dataset.revealTag;
      const line = this.querySelector(`[data-reveal-line="${i}"]`);
      const dot = this.querySelector(`[data-reveal-dot="${i}"]`);
      if (!line || !dot) return;
      const r = tag.getBoundingClientRect();
      const top = r.top - box.top + r.height / 2;
      const leftVisual = (tag.dataset.side === 'start') !== rtl;
      const x1 = leftVisual ? r.right - box.left + 4 : r.left - box.left - 4;
      const y1 = top;
      const spreadX = box.width * 0.1;
      const x2 = leftVisual ? cx - spreadX : cx + spreadX;
      const y2 = y1 + (cy - y1) * 0.35;
      line.setAttribute('x1', String(x1));
      line.setAttribute('y1', String(y1));
      line.setAttribute('x2', String(x2));
      line.setAttribute('y2', String(y2));
      dot.setAttribute('cx', String(x1));
      dot.setAttribute('cy', String(y1));
      const length = Math.hypot(x2 - x1, y2 - y1);
      line.style.setProperty('--len', String(length));
      line.style.setProperty('--d', i);
      dot.style.setProperty('--d', i);
    });
  }
}

if (!customElements.get('s-ingredient-reveal')) {
  customElements.define('s-ingredient-reveal', SIngredientReveal);
}
